import { LegalPage, MailTo, OPERATOR, Section } from "./LegalPage";

/**
 * 18 U.S.C. §2257 / §2257A record-keeping statement.
 *
 * READ BEFORE RELYING ON THIS. The statement below takes the common position for a
 * platform that hosts material created by others: that the operator is not a "producer"
 * as defined at 28 C.F.R. §75.1(c), and that records are held by the original producers.
 *
 * Whether that position holds for THIS service is a question for a lawyer, not for a
 * template, because the ingest pipeline does more than host: it downloads third-party
 * material, transcodes it, cuts renditions, and re-serves it under its own domain. Some
 * readings treat re-encoding and re-publishing as secondary production, which carries its
 * own record-keeping duty. The exemption language here is therefore a starting point to
 * review, not a conclusion to rely on.
 *
 * NO CUSTODIAN IS NAMED, because none has been appointed. 28 C.F.R. §75.6 requires a
 * custodian's name and a physical address at which records may be inspected, and an email
 * address does not satisfy it. So this page does not assert that such records are kept —
 * it rests entirely on the not-a-producer position above and points records requests at
 * the inbox. If that position turns out not to hold, the missing custodian is the gap,
 * and naming a fictional one here would have hidden it rather than closed it.
 */
export function Records2257() {
  return (
    <LegalPage title="18 U.S.C. §2257 Compliance Statement" updated="August 4, 2026">
      <p>
        All models, actors, actresses, and other persons appearing in any visual depiction
        of actual or simulated sexually explicit conduct available on or through goonlords
        were over the age of eighteen (18) years at the time the depiction was created.
      </p>

      <Section title="Content produced by third parties">
        With respect to visual depictions displayed on this website that were not produced
        by {OPERATOR}, the operator is not the &ldquo;producer&rdquo; as that term is defined
        at 28 C.F.R. §75.1(c). Such depictions are made available by third parties, and the
        records required by 18 U.S.C. §2257 and §2257A are maintained by the respective
        producers of that material.
      </Section>

      <Section title="Records requests">
        {OPERATOR} does not produce the material it displays. Requests relating to records
        for any depiction on this site, and enquiries about the material&rsquo;s origin,
        should be sent to <MailTo subject="2257 records request" /> and will be answered or
        directed to the originating producer.
      </Section>

      <Section title="Reporting a violation">
        We do not knowingly host material depicting any person under eighteen, or any person
        who has not consented to its creation and distribution. If you believe any material
        here depicts a minor or a non-consenting person, report it to{" "}
        <MailTo subject="Urgent content report" /> with a link to the material. Reports of
        this kind are handled ahead of everything else, and the material is removed while we
        review.
      </Section>

      <Section title="Exempt material">
        Material consisting only of depictions that are not &ldquo;actual sexually explicit
        conduct&rdquo; as defined at 18 U.S.C. §2256(2)(A)(i)&ndash;(iv), or that was created
        before the effective dates of the applicable regulations, is exempt from the
        record-keeping requirements.
      </Section>
    </LegalPage>
  );
}
